/**
 * Menubalk van werkplek v6 (ontwerp 26, sessie 67).
 *
 * Laptop: een licht vlak van 80 px met icoon en woord eronder. Een klik op
 * een leeg deel van de balk klapt hem uit tot 200 px (woorden naast de
 * iconen, cijfers bij Vandaag en Retouren); een klik op een item of ergens
 * buiten de balk klapt hem weer in. "Menu" onderin opent een klein paneel
 * met de rest.
 *
 * iPhone (onder 900 px): de balk verdwijnt en `MobieleBalk` staat onderin
 * met vijf knoppen; "Meer" schuift een paneel omhoog.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type RefObject,
} from "react";
import { NavLink } from "react-router-dom";

import { useAuthStore } from "@/lib/auth-store";
import { useLogout } from "@/lib/hooks/useLogout";

import { Icoon, type IcoonNaam } from "../Icoon";
import "./menubalk.css";

export interface MenubalkProps {
  /** Aantal open zaken bij Vandaag en te beoordelen retouren. */
  tellers: { vandaag: number; retouren: number };
}

interface Item {
  naar: string;
  woord: string;
  icoon: IcoonNaam;
  teller?: "vandaag" | "retouren";
  /** Alleen voor beheerders (rol admin), zoals de API erachter. */
  alleenBeheer?: boolean;
}

/** De items die deze gebruiker mag zien. Zonder geladen gebruiker geen
 *  beheer-items: liever even weg dan een scherm dat 403 geeft. */
function useZichtbaar(items: Item[]): Item[] {
  const beheerder = useAuthStore((s) => s.gebruiker?.rol === "admin");
  return beheerder ? items : items.filter((i) => !i.alleenBeheer);
}

const HOOFD: Item[] = [
  { naar: "/", woord: "Vandaag", icoon: "vandaag", teller: "vandaag" },
  { naar: "/klanten", woord: "Klanten", icoon: "klanten" },
  { naar: "/retouren", woord: "Retouren", icoon: "retour", teller: "retouren" },
  { naar: "/reviews", woord: "Reviews", icoon: "ster" },
  { naar: "/signalen", woord: "Signalen", icoon: "signaal" },
  { naar: "/kennis", woord: "Kennis", icoon: "kennis" },
  { naar: "/gezondheid", woord: "Gezondheid", icoon: "hart" },
  { naar: "/beheer", woord: "Beheer", icoon: "beheer" },
];

const MENU: Item[] = [
  { naar: "/voorraad", woord: "Voorraad", icoon: "voorraad" },
  { naar: "/profiel", woord: "Profiel", icoon: "klant" },
  { naar: "/beheer/gebruikers", woord: "Gebruikers en toegang", icoon: "klanten" },
  { naar: "/beheer/afwezigheid", woord: "Afwezigheid", icoon: "vandaag" },
];

const MOBIEL: Item[] = [
  { naar: "/", woord: "Vandaag", icoon: "vandaag" },
  { naar: "/klanten", woord: "Klanten", icoon: "klanten" },
  { naar: "/retouren", woord: "Retouren", icoon: "retour" },
  { naar: "/signalen", woord: "Signalen", icoon: "signaal" },
];

const MEER: Item[] = [
  { naar: "/reviews", woord: "Reviews", icoon: "ster" },
  { naar: "/kennis", woord: "Kennis", icoon: "kennis" },
  { naar: "/gezondheid", woord: "Gezondheid", icoon: "hart" },
  { naar: "/beheer", woord: "Beheer", icoon: "beheer" },
  { naar: "/voorraad", woord: "Voorraad", icoon: "voorraad" },
  { naar: "/profiel", woord: "Profiel", icoon: "klant" },
  { naar: "/beheer/gebruikers", woord: "Gebruikers en toegang", icoon: "klanten" },
  { naar: "/beheer/afwezigheid", woord: "Afwezigheid", icoon: "vandaag" },
];

const aan = ({ isActive }: { isActive: boolean }) => (isActive ? "on" : undefined);

/** Sluit bij een klik buiten `ref` of bij Escape, zolang `actief` waar is. */
function useBuitenKlik(
  ref: RefObject<HTMLElement>,
  actief: boolean,
  sluit: () => void,
) {
  useEffect(() => {
    if (!actief) return;
    const klik = (e: globalThis.MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) sluit();
    };
    const toets = (e: KeyboardEvent) => {
      if (e.key === "Escape") sluit();
    };
    document.addEventListener("click", klik);
    document.addEventListener("keydown", toets);
    return () => {
      document.removeEventListener("click", klik);
      document.removeEventListener("keydown", toets);
    };
  }, [ref, actief, sluit]);
}

export function Menubalk({ tellers }: MenubalkProps) {
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const houder = useRef<HTMLDivElement>(null);
  const { logout, bezig } = useLogout();
  const menu = useZichtbaar(MENU);

  const sluitAlles = useCallback(() => {
    setOpen(false);
    setMenuOpen(false);
  }, []);
  useBuitenKlik(houder, open || menuOpen, sluitAlles);

  // Klik op een leeg deel van de balk: uit- of inklappen. Items en de
  // menuknop regelen hun eigen klik.
  const opBalk = (e: MouseEvent<HTMLElement>) => {
    if ((e.target as HTMLElement).closest("a, button")) return;
    setOpen((o) => !o);
  };

  return (
    <div className="v6-railhouder" ref={houder}>
      <nav
        className={`v6-vlak v6-rail${open ? " open" : ""}`}
        aria-label="Menu"
        data-open={open ? "ja" : "nee"}
        onClick={opBalk}
      >
        <div className="v6-rail-logo">
          <span className="merk">VO</span>
          <span className="tekst">Klantenservice</span>
        </div>
        {HOOFD.map((item) => (
          <NavLink
            key={item.naar}
            to={item.naar}
            end={item.naar === "/"}
            className={aan}
            title={open ? undefined : item.woord}
            onClick={sluitAlles}
          >
            <Icoon naam={item.icoon} />
            <span className="woord">{item.woord}</span>
            {open && item.teller ? (
              <span className="n num" data-teller={item.teller}>
                {tellers[item.teller]}
              </span>
            ) : null}
          </NavLink>
        ))}
        <button
          type="button"
          className={`uit${menuOpen ? " on" : ""}`}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          onClick={() => setMenuOpen((m) => !m)}
        >
          <Icoon naam="menu" />
          <span className="woord">Menu</span>
        </button>
      </nav>
      {menuOpen ? (
        <div className="v6-railmenu" role="menu" aria-label="Meer">
          {menu.map((item) => (
            <NavLink
              key={item.naar}
              to={item.naar}
              role="menuitem"
              className={aan}
              onClick={sluitAlles}
            >
              <Icoon naam={item.icoon} klein />
              <span>{item.woord}</span>
            </NavLink>
          ))}
          <button
            type="button"
            role="menuitem"
            disabled={bezig}
            onClick={() => {
              sluitAlles();
              void logout();
            }}
          >
            <Icoon naam="uit" klein />
            <span>Uitloggen</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function MobieleBalk(_props: MenubalkProps) {
  const [meer, setMeer] = useState(false);
  const { logout, bezig } = useLogout();
  const meerItems = useZichtbaar(MEER);
  const sluit = useCallback(() => setMeer(false), []);

  useEffect(() => {
    if (!meer) return;
    const toets = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMeer(false);
    };
    document.addEventListener("keydown", toets);
    return () => document.removeEventListener("keydown", toets);
  }, [meer]);

  return (
    <nav className="v6-mobbar" aria-label="Menu onderin">
      {MOBIEL.map((item) => (
        <NavLink
          key={item.naar}
          to={item.naar}
          end={item.naar === "/"}
          className={aan}
          onClick={sluit}
        >
          <Icoon naam={item.icoon} />
          <span>{item.woord}</span>
        </NavLink>
      ))}
      <button
        type="button"
        className={meer ? "on" : undefined}
        aria-expanded={meer}
        onClick={() => setMeer((m) => !m)}
      >
        <Icoon naam="menu" />
        <span>Meer</span>
      </button>
      {meer ? (
        <>
          <div className="v6-meer-achter" onClick={sluit} aria-hidden="true" />
          <div className="v6-meer" role="dialog" aria-label="Meer">
            <button type="button" className="v6-meer-dicht" aria-label="Sluiten" onClick={sluit}>
              <Icoon naam="x" />
            </button>
            <div className="v6-meer-lijst">
              {meerItems.map((item) => (
                <NavLink key={item.naar} to={item.naar} className={aan} onClick={sluit}>
                  <Icoon naam={item.icoon} />
                  <span>{item.woord}</span>
                </NavLink>
              ))}
              <button
                type="button"
                disabled={bezig}
                onClick={() => {
                  sluit();
                  void logout();
                }}
              >
                <Icoon naam="uit" />
                <span>Uitloggen</span>
              </button>
            </div>
          </div>
        </>
      ) : null}
    </nav>
  );
}
